import { Icon, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import UiCard from "components/base/UiCard";
import UiTypography from "components/base/UiTypography";
import { ProductType, ProposalPlantProduct, ProposalProduct } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import PlantProductPanel from "./PlantProductPanel";
import { draftColors } from "./draftDesignTokens";
import { snakeCase } from "change-case";

const HIDE_DEPTH_TYPES = new Set([
  snakeCase(ProductType.CONCRETE_CURB_OR_GUTTER),
  snakeCase(ProductType.CONCRETE_COLUMN),
  snakeCase(ProductType.CONCRETE_WALL),
  snakeCase(ProductType.CONCRETE_FOOTING),
  snakeCase(ProductType.ASPHALT_CURBING),
]);

const SHOW_CONFIGURE_MATERIAL_TYPES = new Set([...HIDE_DEPTH_TYPES]);

type MaterialTruckingSectionProps = {
  product: ProposalProduct;
  onConfigureTrucking?: () => void;
  onAddMaterial?: () => void;
  onDeletePlantProduct?: (plantProductId: string) => void;
  onOpenMaterialConfig?: () => void;
};

export default function MaterialTruckingSection({
  product,
  onConfigureTrucking,
  onAddMaterial,
  onDeletePlantProduct,
  onOpenMaterialConfig,
}: MaterialTruckingSectionProps) {
  const materialCostTotal = +(product.costMaterial ?? 0) + +(product.costTransferTruck ?? 0);
  const plantProducts: ProposalPlantProduct[] = (product.proposalPlantProducts ??
    []) as ProposalPlantProduct[];
  const productType = product.material?.type ?? "";
  const hideDepth = HIDE_DEPTH_TYPES.has(productType);
  const showConfigureMaterial = SHOW_CONFIGURE_MATERIAL_TYPES.has(productType);
  const hasAttributes =
    Object.keys(product.material?.attributes ?? {}).filter((k) => k !== "__typename").length > 0;

  return (
    <UiCard variant="section">
      <MDBox
        px="8px"
        py="8px"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap="10px"
      >
        <UiTypography uiVariant="overline" sx={{ color: draftColors.textAction }}>
          Material &amp; Trucking
        </UiTypography>
        <MDBox display="flex" gap="6px" alignItems="center">
          <Tooltip title="Add Material" placement="top" arrow>
            <MDBox
              sx={{ color: draftColors.green, cursor: "pointer", display: "flex" }}
              onClick={onAddMaterial}
            >
              <Icon fontSize="small">add</Icon>
            </MDBox>
          </Tooltip>
          {showConfigureMaterial && hasAttributes && (
            <Tooltip title="Configure Material" placement="top" arrow>
              <MDBox
                sx={{ color: draftColors.textSecondary, cursor: "pointer", display: "flex" }}
                onClick={onOpenMaterialConfig}
              >
                <Icon fontSize="small">settings</Icon>
              </MDBox>
            </Tooltip>
          )}
          <Tooltip title="Configure Trucking" placement="top" arrow>
            <MDBox
              sx={{ color: draftColors.textSecondary, cursor: "pointer", display: "flex" }}
              onClick={onConfigureTrucking}
            >
              <Icon fontSize="small">edit</Icon>
            </MDBox>
          </Tooltip>
          <UiTypography uiVariant="accent-bold">{formatCentsToUSD(materialCostTotal)}</UiTypography>
        </MDBox>
      </MDBox>

      <MDBox sx={{ px: "8px", py: "8px", display: "flex", flexDirection: "column", gap: "12px" }}>
        {plantProducts.map((pp, index) => (
          <PlantProductPanel
            key={pp.id}
            pp={pp}
            index={index}
            productId={product.id}
            onDelete={onDeletePlantProduct}
            measurementMaterial={product.material?.measurementMaterial}
            productType={productType}
            measurementJob={product.material?.measurementJob}
            hideDepth={hideDepth}
          />
        ))}
      </MDBox>

      {plantProducts.length === 0 && (
        <MDBox
          sx={{
            borderTop: `1px solid ${draftColors.border}`,
            px: "8px",
            py: "16px",
          }}
        >
          <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
            No plant products configured yet. Use the + icon to add materials.
          </UiTypography>
        </MDBox>
      )}
    </UiCard>
  );
}
